/**
 * 18ヶ月の中身。トップページの2つのセクションが同じここを見る。
 *
 *   LearningPhases — 6フェーズを横に並べて「順番」を見せる
 *   PhaseDetail    — 選んだフェーズの中の章を出して「実際に何をやるか」に答える
 *
 * 章と歩数は学習ポータルの実物をそのまま写した。全18章 261ステップ。
 * @noiman/shegate 側にも 6フェーズ / 12セクションの表があるが、あちらは
 * カリキュラムページ用に作った粒度で、ポータルの章立てとは呼び方が違う。
 * 2か所で名前が割れるのが一番まずいので、トップページはこの1本に寄せている。
 *
 * 章がどのフェーズに入るかは、章の並び順と各フェーズの月数から決めた。
 * 章は前から順に進むので、区切りの位置以外の入れ方はない。
 * ここだけは実物の対応表を見て決めたわけではないので、
 * ポータル側で章順が変わったら合わせて直す。
 *
 * 進捗 (21/25 など) は受講生ごとの数字なので持ち込まない。
 */

export type StepKind = "学習" | "実践" | "宿題";

export type Step = {
  kind: StepKind;
  title: string;
};

export type Chapter = {
  no: number;
  title: string;
  /** その章の総ステップ数 */
  steps: number;
  /** ポータルの章カードに出ている先頭の数件 */
  samples: Step[];
};

/** 難易度。うちのカリキュラムなので、どこが基礎でどこが実践かはこちらで決められる */
export const LEVELS = {
  basic: { label: "基礎", className: "bg-[#5b6b76] text-white" },
  applied: { label: "応用", className: "bg-shegate-accent text-white" },
  practical: { label: "実践", className: "bg-[#1a1a1a] text-white" },
} as const;

export type Level = keyof typeof LEVELS;

export type Phase = {
  no: string;
  level: Level;
  months: string;
  /** 幅の狭い帯に入れる短い名前。長い名前が入らない所で使う */
  short: string;
  /** 段の中の章を、短い名前にした札。2〜4個。章の中身から外れない言い換えだけ */
  tags: string[];
  /** 何ヶ月目から何ヶ月目か。俯瞰図の帯の幅がこれで決まる */
  from: number;
  to: number;
  title: string;
  /** そのフェーズを終えたときに作れるもの。教材の画面ではなく成果物を出す。
   *  alt は空。見出しと can で内容が伝わるので、読み上げの流れからは外す */
  image: string;
  /** 画像に付ける一行。「何ができるようになるか」 */
  can: string;
  summary: string;
  stack: string[];
  milestone?: string;
  chapters: Chapter[];
};

export const PHASES: Phase[] = [
  {
    no: "01",
    image: "/assets/platform_terminal.webp",
    can: "コマンドでサーバーを操作できる",
    level: "basic",
    months: "1〜3ヶ月",
    from: 1,
    to: 3,
    title: "コマンド操作・アルゴリズム",
    short: "コンピュータサイエンス",
    tags: ["ITリテラシー", "AI活用", "Linux", "OSの仕組み"],
    summary: "コンピュータが何をしているかを先に理解し、サーバーを自分の手で触ります。",
    stack: ["コンピュータサイエンス", "アルゴリズム", "Linux", "Git"],
    chapters: [
      {
        no: 1,
        title: "はじめに編",
        steps: 17,
        samples: [
          { kind: "学習", title: "ポータルサイトへようこそ！！" },
          { kind: "実践", title: "Mattermostに入ろう（コミュニティ）" },
          { kind: "実践", title: "自己紹介してみよう" },
        ],
      },
      {
        no: 2,
        title: "IT・開発リテラシー編",
        steps: 13,
        samples: [
          { kind: "学習", title: "プログラミング学習のはじめ方を知ろう" },
          { kind: "学習", title: "エンジニアキャリアの歩き方を知ろう 1" },
          { kind: "学習", title: "エンジニアキャリアの歩き方を知ろう 2" },
        ],
      },
      {
        no: 3,
        title: "AI活用編",
        steps: 17,
        samples: [
          { kind: "実践", title: "生成AI活用基礎（ChatGPT / Claude）" },
          { kind: "学習", title: "AIへの指示の出し方 プロンプト入門" },
          { kind: "学習", title: "基本的なプロンプト設計パターン Zero-shotプロンプティングとは" },
        ],
      },
      {
        no: 4,
        title: "Linux基礎編",
        steps: 7,
        samples: [
          { kind: "学習", title: "Linuxを学ぼう" },
          { kind: "学習", title: "Linux入門 コマンド操作のきほん" },
          { kind: "実践", title: "Linux基本コマンド（ls, cd, mkdir, cp, mv, rm）" },
        ],
      },
      {
        no: 5,
        title: "コンピュータサイエンス基礎編",
        steps: 10,
        samples: [
          { kind: "実践", title: "コンピュータサイエンス（アーキテクチャ＆OS）" },
          { kind: "実践", title: "コンピュータサイエンス（データベース＆ストレージ）" },
          { kind: "実践", title: "コンピュータサイエンス（設計）" },
        ],
      },
    ],
  },
  {
    no: "02",
    image: "/assets/ecommerce_01.webp",
    can: "商品が買えるECサイトが動く",
    level: "basic",
    months: "4〜7ヶ月",
    from: 4,
    to: 7,
    title: "データベース設計・安全なログイン",
    short: "DB設計",
    tags: ["SQL", "Python", "Java", "ECサイト"],
    summary: "画面の裏側を作ります。データをどう持つかを設計するところまでやります。",
    stack: ["Java", "DB設計", "SQL", "JSP / Tomcat"],
    chapters: [
      {
        no: 6,
        title: "データベース・SQL編",
        steps: 18,
        samples: [
          { kind: "実践", title: "SQLとは？" },
          { kind: "実践", title: "SELECT文でデータを取得しよう" },
          { kind: "実践", title: "INSERT / UPDATE / DELETE を学ぼう" },
        ],
      },
      {
        no: 7,
        title: "Python入門編",
        steps: 14,
        samples: [
          { kind: "学習", title: "Python基礎コース" },
          { kind: "実践", title: "制御構造（if / for / while）を学ぼう" },
          { kind: "実践", title: "Python環境構築（pip / venv）" },
        ],
      },
      {
        no: 8,
        title: "Git基礎編",
        steps: 7,
        samples: [
          { kind: "学習", title: "Git/GitHub基礎コース" },
          { kind: "実践", title: "Gitのインストールと初期設定" },
          { kind: "実践", title: "Gitの基本操作（add, commit, push, pull）" },
        ],
      },
      {
        no: 9,
        title: "バックエンド開発編",
        steps: 25,
        samples: [
          { kind: "学習", title: "Java基礎コース" },
          { kind: "実践", title: "Java開発環境のセットアップ" },
          { kind: "実践", title: "変数・データ型・演算子" },
        ],
      },
      {
        no: 10,
        title: "ECサイト構築編",
        steps: 5,
        samples: [
          { kind: "学習", title: "JavaScript入門 基礎文法編" },
          { kind: "宿題", title: "ECサイトのアーキテクチャ図を作ろう" },
          { kind: "実践", title: "ECサイトの最終仕上げとデモ準備" },
        ],
      },
    ],
  },
  {
    no: "03",
    image: "/assets/task_01.webp",
    can: "APIとつないだ画面が作れる",
    level: "basic",
    months: "8〜10ヶ月",
    from: 8,
    to: 10,
    title: "Webサイト制作・Reactで作成する",
    short: "Web / React",
    tags: ["モダンWeb", "タスク管理"],
    summary: "自分で作った API を、自分で作った画面から呼びます。",
    stack: ["React", "TypeScript", "HTML / CSS", "API連携"],
    chapters: [
      {
        no: 11,
        title: "モダンWeb・フルスタック編",
        steps: 23,
        samples: [
          { kind: "実践", title: "HTML5の基本構造を学ぼう" },
          { kind: "実践", title: "HTMLのフォーム要素を学ぼう" },
          { kind: "実践", title: "CSS3の基礎（セレクタ・ボックスモデル）" },
        ],
      },
      {
        no: 12,
        title: "タスク管理アプリ編",
        steps: 2,
        samples: [{ kind: "宿題", title: "タスク管理アプリのデモとコードを提出しよう" }],
      },
    ],
  },
  {
    no: "04",
    image: "/assets/aichat_1.webp",
    can: "AIが答えるチャットを組み込める",
    level: "applied",
    months: "11〜13ヶ月",
    from: 11,
    to: 13,
    title: "AI機能の組み込み・RAG構築",
    short: "AI組み込み",
    tags: ["AI開発", "チャットアプリ"],
    summary: "AIを使う側ではなく、AIを組み込んだ機能を作る側に回ります。",
    stack: ["RAG", "LangChain", "Dify", "プロンプト設計"],
    chapters: [
      {
        no: 13,
        title: "AIエンジニアリング編",
        steps: 21,
        samples: [
          { kind: "実践", title: "Python復習 基礎文法のおさらい" },
          { kind: "学習", title: "Pythonオブジェクト指向コース" },
          { kind: "学習", title: "Python AI・データ分析コース" },
        ],
      },
      {
        no: 14,
        title: "AIチャットアプリ編",
        steps: 3,
        samples: [
          { kind: "宿題", title: "AIチャットアプリのデモとコードを提出しよう" },
          { kind: "実践", title: "クイズ 生成AI & AIエンジニアリング総合チェック" },
        ],
      },
    ],
  },
  {
    no: "05",
    image: "/assets/platform_terminal.webp",
    can: "作ったものを公開して動かし続けられる",
    level: "applied",
    months: "14〜15ヶ月",
    from: 14,
    to: 15,
    title: "サーバー構築・クラウド公開",
    short: "クラウド",
    tags: ["インフラ", "データ分析"],
    summary: "作ったものを、人が使える場所に置いて動かし続けます。",
    stack: ["Linux", "AWS", "CI / CD"],
    milestone: "15ヶ月目 AWS SAA 取得",
    chapters: [
      {
        no: 15,
        title: "インフラ・クラウド編",
        steps: 6,
        samples: [
          { kind: "学習", title: "Docker基礎コース" },
          { kind: "実践", title: "Dockerのインストールと基本概念" },
          { kind: "実践", title: "Dockerfileの書き方とイメージのビルド" },
        ],
      },
      {
        no: 16,
        title: "分析ダッシュボード編",
        steps: 14,
        samples: [
          { kind: "宿題", title: "Docker Composeで開発環境を構築しよう" },
          { kind: "実践", title: "分析用データベースとETL処理の設計" },
          { kind: "実践", title: "Webサーバー（Nginx）の導入" },
        ],
      },
    ],
  },
  {
    no: "06",
    image: "/assets/kikakusho_01.webp",
    can: "企画書から作り、人前で説明できる",
    level: "practical",
    months: "16〜18ヶ月",
    from: 16,
    to: 18,
    title: "要件定義・チーム開発",
    short: "要件定義",
    tags: ["要件定義", "チーム開発", "キャリア"],
    summary: "「何を作るか」から決めます。要件定義から動くものまで通します。",
    stack: ["要件定義", "基本設計", "チーム開発", "ポートフォリオ"],
    chapters: [
      {
        no: 17,
        title: "実践・キャリア編",
        steps: 25,
        samples: [
          { kind: "実践", title: "VPC（ネットワーク設計・サブネット・セキュリティグループ）" },
          { kind: "学習", title: "ポートフォリオの作り方 見せ方と面接でのプレゼンテーション" },
          { kind: "実践", title: "要件定義の進め方（顧客ヒアリング手法）" },
        ],
      },
      {
        no: 18,
        title: "卒業・18ヶ月完了編",
        steps: 34,
        samples: [
          { kind: "実践", title: "講師と面談しましょう！" },
          { kind: "宿題", title: "クラス図を作成しよう" },
          { kind: "宿題", title: "シーケンス図を作成しよう" },
        ],
      },
    ],
  },
];

export const ALL_CHAPTERS = PHASES.flatMap((phase) => phase.chapters);

/** 全18章の歩数の合計。数えて出すので、章を足しても直さなくていい */
export const TOTAL_STEPS = ALL_CHAPTERS.reduce((sum, chapter) => sum + chapter.steps, 0);

/** 全体の月数。俯瞰図がこれを分母にする */
export const TOTAL_MONTHS = PHASES[PHASES.length - 1].to;

/** そのフェーズが全体の何割を占めるか */
export function phaseWidth(phase: Phase): string {
  return `${((phase.to - phase.from + 1) / TOTAL_MONTHS) * 100}%`;
}
